<template>
  <div class="progress-circle" :class="sizeClass">
    <svg class="progress-circle__ring" :viewBox="viewBox">
      <circle
        class="progress-circle__bg"
        :cx="center"
        :cy="center"
        :r="radius"
        fill="none"
        :stroke="bgStroke"
        :stroke-width="strokeWidth"
      />
      <circle
        class="progress-circle__fill"
        :cx="center"
        :cy="center"
        :r="radius"
        fill="none"
        :stroke="fillStroke"
        :stroke-width="strokeWidth"
        :stroke-dasharray="`${(progress * circumference) / 100}, ${circumference}`"
        :transform="`rotate(-90 ${center} ${center})`"
      />
    </svg>
    <span v-if="showText" class="progress-circle__text">{{ Math.round(progress) }}%</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

interface Props {
  progress: number;
  size?: "small" | "medium" | "large";
  bgStroke?: string;
  fillStroke?: string;
  showText?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  progress: 0,
  size: "medium",
  bgStroke: "rgba(255, 255, 255, 0.2)",
  fillStroke: "white",
  showText: true,
});

const sizeConfig = {
  small: { size: 24, center: 12, radius: 10, circumference: 62.83 },
  medium: { size: 48, center: 18, radius: 16, circumference: 100.53 },
  large: { size: 64, center: 32, radius: 28, circumference: 175.93 },
};

const config = computed(() => sizeConfig[props.size]);
const viewBox = computed(() => `0 0 ${config.value.size} ${config.value.size}`);
const center = computed(() => config.value.center);
const radius = computed(() => config.value.radius);
const circumference = computed(() => config.value.circumference);
const sizeClass = computed(() => `progress-circle--${props.size}`);
const strokeWidth = 2;
</script>

<style scoped>
.progress-circle {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-circle__ring {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
  will-change: contents;
  contain: layout style paint;
}

.progress-circle__bg {
  transition: stroke 0.3s ease;
}

.progress-circle__fill {
  transition: stroke-dasharray 0.1s linear;
  stroke-linecap: round;
  will-change: stroke-dasharray;
}

.progress-circle__text {
  position: absolute;
  font-size: 0.625rem;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.progress-circle--small {
  width: 24px;
  height: 24px;
}

.progress-circle--small .progress-circle__text {
  font-size: 0.5rem;
}

.progress-circle--medium {
  width: 48px;
  height: 48px;
}

.progress-circle--large {
  width: 64px;
  height: 64px;
}

.progress-circle--large .progress-circle__text {
  font-size: 0.75rem;
}
</style>
